/* SystemConfigurationComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");
const TestMailComponent = require("./testEmailVeil/TestEmailComponent");
const Renderer = require("./SystemConfigurationRenderer");
const GSO_ID = "565a65db-54e7-4461-a954-f0f38f310e19";

/**
 * @class SystemConfigurationComponent
 * @extends EventEmitter
 *
 * @fires SystemConfigurationComponent#Shutdown
 * @fires SystemConfigurationComponent#DataUpdated
 * @fires SystemConfigurationComponent#DirtyState
 * @fires SystemConfigurationComponent#OrganizationDataChange
 */

class SystemConfigurationComponent extends EventEmitter {
    constructor(
        OrganizationService,
        userSession,
        logger,
        userFeedback,
        SystemConfig,
        userService,
        uiutils,
        Router,
        ServiceConnection
    ) {
        super();
        this._OrganizationService = OrganizationService;
        this._userSession = userSession;
        this._logger = logger;
        this._modalUserFeedback = userFeedback;
        this._SystemConfig = SystemConfig;
        this._userService = userService;
        this._uiutils = uiutils;
        this._router = Router;
        this._ServiceConnectionEntityDef = ServiceConnection;

        this._userEmail = this._userSession.user.email;

        this._systemConfigEntity = undefined;
        this._emailConfig = undefined;
        this._proxyConfig = undefined;

        this._genAiEnabled = undefined;

        this._isSmtpDirty = false;
        this._isProxyDirty = false;
        // Objects to store changes
        this._updatedEmailConfig = undefined;
        this._updatedProxyConfig = undefined;

        this._orgId = _.get(userSession, "activeOrganization.id");
        this._isDirty = false;
        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenModelIsReady() {
        if (!_.isUndefined(this.activeOrgId)) {
            return Promise.all([
                this.getMyUser(),
                this.getSystemConfig(),
                this._getConnectionEntities(),
            ]).then((values) => {
                this._systemConfigEntity = values[1];
                this._smtpServiceConnectionEntities = values[2] || [];

                this._smtpServiceConnectionOpts = this.getSmtpServiceConnectionOptions(
                    this._smtpServiceConnectionEntities
                );

                this._emailConfig = this._systemConfigEntity.getEmailConfig();
                this._proxyConfig = this._SystemConfig._cleanHttpConfig(
                    this._systemConfigEntity.getHttpConfig()
                );
                this._dcEnabled =
                    this._systemConfigEntity._pristineProperties.enableDistributionCenter;

                // Gen AI Enable/ Disable Global property
                this._genAiEnabled = this._systemConfigEntity.getEnableGenAi();

                if (this._proxyConfig.advanced) {
                    this._proxyConfig = this._SystemConfig.convertBasicHttpConfigToAdvanced(
                        this._proxyConfig
                    );
                }
                this._updatedEmailConfig = _.cloneDeep(this._emailConfig);
                this._updatedProxyConfig = _.cloneDeep(this._proxyConfig);
                this._updatedDcEnabled = this._dcEnabled;
                this._updatedGenAiEnabled = this._genAiEnabled;
            });
        } else {
            return Promise.reject();
        }
    }

    shutdown() {
        /**
         * @event SystemConfigurationComponent#Shutdown
         */
        this.emit("Shutdown");
        this._unregisterListeners();
        this.removeAllListeners();
        return Promise.all([this._renderer.shutdown()]).then(
            () => {
                this._systemConfigEntity = undefined;
                this._SystemConfig = undefined;
                this._isDirty = undefined;
                this._emailConfig = undefined;
                this._proxyConfig = undefined;
                this._updatedEmailConfig = undefined;
                this._updatedProxyConfig = undefined;
                this._isSmtpDirty = undefined;
                this._isProxyDirty = undefined;
                this._uiutils = undefined;
                this._userEmail = undefined;
                this._genAiEnabled = undefined;
                this._updatedGenAiEnabled = undefined;
                this._ServiceConnectionEntityDef = undefined;
                this._smtpServiceConnectionEntities = undefined;
                this._smtpServiceConnectionOpts = undefined;
            },
            (err) => {
                console.log("Error shutting down CustomFieldsComponent: ", err);
            }
        );
    }

    get logger() {
        return this._logger;
    }

    get userFeedback() {
        return this._modalUserFeedback;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    organizationChanged({id, name}) {
        if (this._orgId === id) {
            return;
        }
        this._orgId = id;
        this._getConnectionEntities()
            .then((entities) => {
                this._smtpServiceConnectionEntities = entities || [];
                this._smtpServiceConnectionOpts = this.getSmtpServiceConnectionOptions(entities);
            })
            .finally(() => {
                this._triggerOrganization({id, name});
            });
    }

    _triggerDataUpdated() {
        /**
         * @event SystemConfigurationComponent#DataUpdated
         */
        this.emit("DataUpdated");
    }

    _triggerOrganization(org) {
        /**
         * @event SystemConfigurationComponent#Organization
         * @param {{id: string, name: string}} - new organization parameters.
         */
        this.emit("Organization", org);
    }

    _triggerOrganizationDataChanged(orgData) {
        /**
         * @event SystemConfigurationComponent#OrganizationDataChange
         * @param {Object} - new organization data.
         */
        this.emit("OrganizationDataChange", orgData);
    }

    getActiveOrgId() {
        return this._orgId;
    }

    get myUserInfo() {
        return this._myUserInfo;
    }

    getSystemConfig() {
        return this._SystemConfig.loadSystemConfig(GSO_ID);
        // return sysDetailsFeed.whenInitialized();
    }

    getSmtpServiceConnectionOptions(entities) {
        return entities.map((entity) => {
            return {
                label: entity.name,
                value: entity.id,
            };
        });
    }

    getMyUser() {
        let userRequest = {
            userId: this._userSession.getUser().getId(),
            descendantOrgActionGroupId: this._orgId,
        };
        return this._userService.get(userRequest).then(
            (myInfo) => {
                this._myUserInfo = myInfo;
                _.set(
                    this.defaultProperties,
                    "planPrefs.catalogCreationAuthGroup",
                    _.keys(myInfo.availableActionGroups)[0]
                );
            },
            (err) => {
                console.log("I am unable to request your user info.", err);
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _saveSmtp() {
        this._systemConfigEntity.setEmailConfig(this._updatedEmailConfig);
        return this._systemConfigEntity
            .update()
            .then(() => {
                this._emailConfig = this._systemConfigEntity.getEmailConfig();
                this._updatedEmailConfig = _.cloneDeep(this._emailConfig);
                this._isSmtpDirty = false;
                return this._emailConfig;
            })
            .catch((err) => {
                this._logger.warn(err);
                this._modalUserFeedback.displayError({
                    errorCode: "Save Err",
                    errorMsg: "Unable to save SMTP configuration",
                    err,
                });
            });
    }

    _setHttpAdvanced(isAdvanced) {
        let wasAdvanced = _.get(this._updatedProxyConfig, "advanced", false);
        if (!wasAdvanced && isAdvanced) {
            this._updatedProxyConfig = this._SystemConfig.convertBasicHttpConfigToAdvanced(
                this._updatedProxyConfig
            );
        }
        _.set(this._updatedProxyConfig, "advanced", isAdvanced);
    }

    _saveProxy() {
        this._systemConfigEntity.setHttpConfig(this._updatedProxyConfig);
        return this._systemConfigEntity
            .update()
            .then(() => {
                this._updatedProxyConfig = this._systemConfigEntity.getHttpConfig();
                this._proxyConfig = _.cloneDeep(this._updatedProxyConfig);
                this._isProxyDirty = false;
                return Promise.resolve(this._proxyConfig);
            })
            .catch((err) => {
                this._logger.warn(err);
                this._modalUserFeedback.displayError({
                    errorCode: "Save Err",
                    errorMsg: "Unable to save Proxy configuration",
                    err,
                });
            });
    }

    _testProxy() {
        return this._SystemConfig
            .testDistributionCenterConnection(this._updatedProxyConfig)
            .then((response) => {
                let message = "Failed to connect using proxy.";
                if (response) {
                    if (response.result && response.result.toLowerCase() === "error") {
                        if (typeof response.error === "string") {
                            message = response.error;
                        } else {
                            message = _.get(
                                response,
                                "error.error",
                                "Failed to connect using proxy."
                            );
                        }
                    } else {
                        message = "Successfully connected using proxy.";
                    }
                }
                // message =
                // !response ? "Failed to send a test email." :  (response.result === "error" ? response.error : "Successfully connected to Gluware Distribution Center.");
                this._uiutils.showMessageDialog({
                    message: message,
                    confirmButtonName: "OK",
                });
            })
            .catch(() => {
                this._uiutils.showMessageDialog({
                    message: "Failed to connect using proxy.",
                    confirmButtonName: "OK",
                });
            });
    }

    _saveDC() {
        this._systemConfigEntity.setProperty("enableDistributionCenter", this._updatedDcEnabled);
        return this._systemConfigEntity
            .update()
            .then(() => {
                this._updatedDcEnabled =
                    this._systemConfigEntity._pristineProperties.enableDistributionCenter;
                this._dcEnabled = this._updatedDcEnabled;
                this._isDCDirty = false;
                return Promise.resolve(this._dcEnabled);
            })
            .catch((err) => {
                this._logger.warn(err);
                this._modalUserFeedback.displayError({
                    errorCode: "Save Err",
                    errorMsg: "Unable to enable/disable Distribution center",
                    err,
                });
            });
    }

    _saveEnableGenAi() {
        const msg = this._updatedGenAiEnabled
            ? "Enabling Gluware Titan Co-Pilot adds this feature to all organizations. " +
              " Are you sure you want to enable access? The browser will be refreshed."
            : "Disabling Gluware Titan Co-Pilot removes this feature from all organizations. " +
              " Are you sure you want to disable access? The browser will be refreshed.";
        this._uiutils
            .showDeleteConfirmDialog({
                message: msg,
                confirmButtonName: "Yes",
                cancelButtonName: "No",
            })
            .then(() => {
                this._systemConfigEntity.setEnableGenAi(this._updatedGenAiEnabled);

                return this._systemConfigEntity
                    .update()
                    .then(() => {
                        this._updatedGenAiEnabled =
                            this._systemConfigEntity._pristineProperties.enableGenAi;
                        this._genAiEnabled = this._updatedGenAiEnabled;
                        this._isGenAiDirty = false;
                        return Promise.resolve(this._genAiEnabled);
                    })
                    .then(() => {
                        this._router.reload();
                    })
                    .catch((err) => {
                        this._logger.warn(err);
                        this._modalUserFeedback.displayError({
                            errorCode: "Save Err",
                            errorMsg: "Failed to enable/disable Gluware Titan Co-Pilot",
                            err,
                        });
                    });
            })
            .catch((error) => {
                if (error && !error.userRejected) {
                    console.log(
                        "Error when saving Gluware Titan Co-Pilot : _saveEnableGenAi()",
                        error
                    );
                    return this._model && this.model.userFeedback.displayError(error);
                }
            });
    }

    _testDistributionCenter() {
        return this._SystemConfig
            .testDistributionCenterConnection(this._proxyConfig)
            .then((response) => {
                let message = "Failed to connect to Gluware Distribution Center.";
                if (response) {
                    if (response.result && response.result.toLowerCase() === "error") {
                        if (typeof response.error === "string") {
                            message = response.error;
                        } else {
                            message = _.get(
                                response,
                                "error.error",
                                "Failed to connect to Gluware Distribution Center."
                            );
                        }
                    } else {
                        message = "Successfully connected to Gluware Distribution Center.";
                    }
                }
                // message =
                // !response ? "Failed to send a test email." :  (response.result === "error" ? response.error : "Successfully connected to Gluware Distribution Center.");
                this._uiutils.showMessageDialog({
                    message: message,
                    confirmButtonName: "OK",
                });
            })
            .catch(() => {
                this._uiutils.showMessageDialog({
                    message: "Failed to connect to Gluware Distribution Center.",
                    confirmButtonName: "OK",
                });
            });
    }
    _testSmtp() {
        const component = new TestMailComponent({
            config: this._updatedEmailConfig,
            entity: this._SystemConfig,
            connectionEntity: this._smtpServiceConnectionEntities.find(
                (entity) => entity.id === this._updatedEmailConfig.serviceConnectionId
            ),
            uiutils: this._uiutils,
            muf: this._modalUserFeedback,
            userEmail: this._userEmail,
            serviceConnectionEntityDef: this._ServiceConnectionEntityDef,
            activeOrgId: this.activeOrgId,
        });
        this._modalUserFeedback.displayCustom(component, {notDismissable: false}).catch((err) => {
            this._logger.warn("Sending test mail error", err);
        });
    }

    _cancelChanges(type) {
        try {
            if (type === "smtp") {
                this._updatedEmailConfig = _.cloneDeep(this._emailConfig);
                this._isSmtpDirty = false;
                return this._emailConfig;
            }
            if (type === "proxy") {
                this._updatedProxyConfig = _.cloneDeep(this._proxyConfig);
                this._isProxyDirty = false;
                return this._proxyConfig;
            }
        } catch (err) {
            this._logger.warn(err);
            if (type === "smtp") {
                return this._updatedEmailConfig;
            }
            if (type === "proxy") {
                return this._updatedProxyConfig;
            }
        }
    }

    _getConnectionEntities() {
        const query = {serviceConnectionTypeName: "svcConnection/smtp.svcConnection"};
        return this._ServiceConnectionEntityDef.loadAscendServiceConnections(
            this.activeOrgId,
            query
        );
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/
    _displayError(err) {
        const errorMsg = err.reduce((msg, err) => {
            return `${msg}\n${err.key}: ${err.err}`;
        }, "");
        this._uiutils.writeErrorMessage(`Error: ${errorMsg}`);
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = SystemConfigurationComponent;

SystemConfigurationComponent.$groupName = "Global";

SystemConfigurationComponent.$displayName = "Configuration";
SystemConfigurationComponent.$license = ["GLUWARE"];

SystemConfigurationComponent.$inject = [
    "$teve.services.instance.Organization",
    "$teve.userSession",
    "$teve.logger",
    "$teve.ui.userFeedback",
    "$teve.entities.SystemConfig",
    "$teve.services.instance.User",
    "$teve.ui.UiUtils",
    "$teve.router",
    "$teve.entities.ServiceConnection",
];
